import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useEffect } from 'react';

export interface EmployerNotification {
  id: string;
  org_id: string;
  notification_type: string;
  title: string;
  body: string | null;
  payload_json: Record<string, unknown> | null;
  read_at: string | null;
  actor_user_id: string | null;
  target_user_id: string | null;
  created_at: string;
}

export function useEmployerNotifications() {
  const { profile, role } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const orgId = profile?.org_id;
  const isAdmin = role === 'org_owner' || role === 'org_admin';

  const query = useQuery({
    queryKey: ['employer-notifications', orgId],
    queryFn: async () => {
      // RLS already handles scoping: admins see all, others see only target_user_id = their id
      const { data, error } = await supabase
        .from('employer_notifications')
        .select('*')
        .eq('org_id', orgId!)
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as EmployerNotification[];
    },
    enabled: !!orgId,
  });

  // Poll for new notifications every 30 seconds (realtime removed for security)
  useEffect(() => {
    if (!orgId) return;
    const interval = setInterval(() => {
      qc.invalidateQueries({ queryKey: ['employer-notifications', orgId] });
    }, 30_000);
    return () => clearInterval(interval);
  }, [orgId, qc]);

  const markRead = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('employer_notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('org_id', orgId!);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employer-notifications', orgId] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('employer_notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('org_id', orgId!)
        .is('read_at', null);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employer-notifications', orgId] });
      toast({ title: 'All notifications marked as read' });
    },
  });

  const unreadCount = (query.data ?? []).filter(n => !n.read_at).length;

  return {
    notifications: query.data ?? [],
    isLoading: query.isLoading,
    unreadCount,
    isAdmin,
    markRead: markRead.mutate,
    markAllRead: markAllRead.mutate,
  };
}
